import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Button } from "@legends/ui";
import {
  ApiError,
  ackFanQuestion,
  pollChat,
  pollQuestionCue,
  pollSentFanQuestions,
  postChat,
  sendFanQuestion,
  type ChatWireMessage,
  type SentFanQuestion,
} from "./api";

// A 401/403 from a long-poll is a PERMANENT authorization failure, not a transient
// blip — retrying it just spams the server (a fan whose session lacks cave_config
// hammered the Director-only /questions/sent thousands of times, LSC-317). Stop the
// loop instead of backing off and retrying forever.
function isPermanentAuthError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

// Live chat relayed through the API (ADR 0026). The Go-Live tab, the director,
// and every ticket-holding viewer post to POST /broadcasts/:id/chat and
// long-poll GET .../chat for new messages. This replaces the old LiveKit-data
// path, which only carried publisher→publisher reliably — so a fan's message
// never reached the legend/director — and it works regardless of delivery mode
// (WebRTC or HLS, where viewers aren't in the room at all).

export interface ChatMessage {
  id: number;
  user: string;
  text: string;
  /** A viewer's "ask the legend" — surfaced to the director. */
  question: boolean;
  /** Sent by us (aligned/coloured differently in the rail). */
  self: boolean;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Subscribe to (and send) a broadcast's chat via the API. `enabled` gates the
 * long-poll (the surface is live/watching); messages de-dupe on their server id.
 */
export function useLiveChat(broadcastId: string | null, enabled: boolean) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const cursorRef = useRef(0);
  const seenRef = useRef<Set<number>>(new Set());
  const mineRef = useRef<Set<number>>(new Set());

  const append = useCallback((w: ChatWireMessage) => {
    if (seenRef.current.has(w.id)) return; // de-dupe (a post + its poll echo)
    seenRef.current.add(w.id);
    if (w.id > cursorRef.current) cursorRef.current = w.id;
    setMessages((m) => [
      ...m,
      { id: w.id, user: w.user, text: w.text, question: w.question, self: mineRef.current.has(w.id) },
    ]);
  }, []);

  // Long-poll for new messages while enabled.
  useEffect(() => {
    if (!enabled || !broadcastId) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      while (!cancelled) {
        try {
          const { messages: msgs } = await pollChat(broadcastId, cursorRef.current, ctrl.signal);
          if (cancelled) break;
          for (const w of msgs) append(w);
          if (msgs.length === 0) await sleep(300); // gentle gap if the server returns fast
        } catch (err) {
          if (cancelled || ctrl.signal.aborted || isPermanentAuthError(err)) break;
          await sleep(1500); // transient error — back off, keep polling
        }
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [enabled, broadcastId, append]);

  const send = useCallback(
    (text: string, question = false) => {
      const t = text.trim();
      if (!t || !broadcastId) return;
      postChat(broadcastId, t, question)
        .then((w) => {
          mineRef.current.add(w.id);
          append(w);
        })
        .catch(() => undefined);
    },
    [broadcastId, append],
  );

  return { messages, send };
}

/** Durable Director-approved Fan questions, shared by Direct and Go Live. */
export function useSentFanQuestions(broadcastId: string | null, enabled: boolean) {
  const [questions, setQuestions] = useState<SentFanQuestion[]>([]);
  const cursorRef = useRef(0);
  const seenRef = useRef<Set<number>>(new Set());

  const append = useCallback((question: SentFanQuestion) => {
    if (seenRef.current.has(question.id)) return;
    seenRef.current.add(question.id);
    if (question.id > cursorRef.current) cursorRef.current = question.id;
    setQuestions((current) => [...current, question]);
  }, []);

  useEffect(() => {
    setQuestions([]);
    cursorRef.current = 0;
    seenRef.current = new Set();
  }, [broadcastId]);

  useEffect(() => {
    if (!enabled || !broadcastId) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      while (!cancelled) {
        try {
          const result = await pollSentFanQuestions(broadcastId, cursorRef.current, ctrl.signal);
          if (cancelled) break;
          for (const question of result.questions) append(question);
          if (result.questions.length === 0) await sleep(300);
        } catch (err) {
          if (cancelled || ctrl.signal.aborted || isPermanentAuthError(err)) break;
          await sleep(1500);
        }
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [enabled, broadcastId, append]);

  const send = useCallback(
    async (messageId: number) => {
      if (!broadcastId) return undefined;
      const question = await sendFanQuestion(broadcastId, messageId);
      append(question);
      return question;
    },
    [broadcastId, append],
  );

  return { questions, latest: questions.length > 0 ? questions[questions.length - 1] : null, send };
}

/**
 * The Legend's advanceable question cue (ADR 0046). Long-polls the server for the
 * current approved, not-yet-acknowledged question + a pending count; `ack` marks
 * the current cue handled and advances to the next one. The cue is derived from
 * the durable store server-side, so it survives refresh and an ack sticks.
 */
export function useQuestionCue(broadcastId: string | null, enabled: boolean) {
  const [cue, setCue] = useState<SentFanQuestion | null>(null);
  const [pending, setPending] = useState(0);
  const sinceRef = useRef(0);

  useEffect(() => {
    setCue(null);
    setPending(0);
    sinceRef.current = 0;
  }, [broadcastId]);

  useEffect(() => {
    if (!enabled || !broadcastId) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      while (!cancelled) {
        try {
          const { question, pending: n } = await pollQuestionCue(broadcastId, sinceRef.current, ctrl.signal);
          if (cancelled) break;
          const changed = (question?.id ?? 0) !== sinceRef.current;
          sinceRef.current = question?.id ?? 0;
          setCue(question);
          setPending(n);
          if (!changed) await sleep(300); // no change (empty or timeout) — gentle gap
        } catch (err) {
          if (cancelled || ctrl.signal.aborted || isPermanentAuthError(err)) break;
          await sleep(1500);
        }
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [enabled, broadcastId]);

  const ack = useCallback(async () => {
    if (!broadcastId || !cue) return;
    // The ack response carries the advanced cue, so the Legend steps immediately;
    // the background poll reconciles from the durable server state after.
    const { question, pending: n } = await ackFanQuestion(broadcastId, cue.id);
    setCue(question);
    setPending(n);
    sinceRef.current = question?.id ?? 0;
  }, [broadcastId, cue]);

  return { cue, pending, ack };
}

const railTitleStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-2xs)",
  fontWeight: 700,
  letterSpacing: "var(--ls-label)",
  textTransform: "uppercase",
  color: "var(--gold)",
};

/**
 * The MVP fan interaction is the moderated "Ask the Legend" workflow only
 * (ADR 0031/0040): general fan-to-fan chat is Not-MVP and is gone from every
 * MVP-facing surface. So `ChatRail` has exactly two variants:
 *
 * - `"ask"` — the fan Ask-the-Legend **submit panel**: a single compose form
 *   that always posts `question:true`, with clear confirmation. It never shows a
 *   feed of other fans' messages (only the fan's own submitted questions).
 * - `"questions"` — the Director's **incoming Fan-questions queue**: a read-only
 *   feed of `question:true` messages only (no fan-to-fan social chat, no compose).
 *   Approve/send still lives in the Director console's own queue section.
 */
export type ChatRailVariant = "ask" | "questions";

export interface ChatRailProps {
  messages: ChatMessage[];
  /** Submit a fan question (always `question:true`) — required for `"ask"`. */
  onSend?: (text: string, question?: boolean) => void;
  variant: ChatRailVariant;
  title?: string;
}

const chatInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  boxSizing: "border-box",
  background: "var(--well)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  color: "var(--text-hi)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-xs)",
  padding: "6px 9px",
  outline: "none",
};

/** Route to the fan submit panel or the director questions feed. */
export function ChatRail({ messages, onSend, variant, title }: ChatRailProps) {
  if (variant === "questions") {
    return <FanQuestionsRail messages={messages} title={title ?? "Fan questions"} />;
  }
  return <AskLegendRail messages={messages} onSend={onSend} title={title ?? "Ask the Legend"} />;
}

/**
 * Fan-facing Ask-the-Legend submit panel. No fan-to-fan feed: the fan can only
 * submit a question (moderated before it reaches the Legend) and see their own
 * submissions echoed back as confirmation.
 */
function AskLegendRail({
  messages,
  onSend,
  title,
}: {
  messages: ChatMessage[];
  onSend?: (text: string, question?: boolean) => void;
  title: string;
}) {
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // Only the fan's OWN questions — never anyone else's messages.
  const mine = messages.filter((m) => m.self && m.question);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onSend?.(t, true);
    setText("");
    setSubmitted(true);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%", minHeight: 0 }}>
      <span style={railTitleStyle}>{title}</span>
      <p style={{ margin: 0, fontSize: "var(--fs-2xs)", color: "var(--text-dim)", lineHeight: 1.4 }}>
        Ask a question — the director reviews questions before they reach the Legend.
      </p>
      <form
        aria-label="Ask the Legend"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        // flex-wrap so the "Ask the Legend" button drops below the input rather than
        // overflowing a narrow phone column (LSC-172); the input keeps flex:1/minWidth:0.
        style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What would you like to ask?"
          aria-label="Your question"
          style={chatInputStyle}
        />
        <Button type="submit" size="sm" variant="gold">
          Ask the Legend
        </Button>
      </form>
      {submitted && (
        <p role="status" style={{ margin: 0, color: "var(--success-hi)", fontSize: "var(--fs-2xs)", lineHeight: 1.4 }}>
          Your question was submitted — the director will review it before it reaches the Legend.
        </p>
      )}
      {mine.length > 0 && (
        <div
          aria-label="Your questions"
          style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}
        >
          {mine.map((m) => (
            <div key={m.id} style={{ fontSize: "var(--fs-xs)", color: "var(--text)" }}>
              <span style={{ fontFamily: "var(--font-sans)", fontWeight: 700, color: "var(--gold)" }}>You asked:</span>{" "}
              {m.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Director-facing incoming Fan-questions queue: a read-only feed of
 * `question:true` messages only. Selecting/approving/sending to the Legend lives
 * in the Director console's own queue section (ADR 0031); this rail is the
 * glanceable stream, never a fan-to-fan social chat.
 */
function FanQuestionsRail({ messages, title }: { messages: ChatMessage[]; title: string }) {
  const questions = messages.filter((m) => m.question);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [questions.length]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%", minHeight: 0 }}>
      <span style={railTitleStyle}>{title}</span>
      <div
        ref={listRef}
        role="log"
        aria-label="Fan questions"
        aria-live="polite"
        style={{ flex: 1, minHeight: 80, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}
      >
        {questions.map((m) => (
          <div key={m.id} style={{ fontSize: "var(--fs-xs)", color: "var(--text)" }}>
            <span style={{ fontFamily: "var(--font-sans)", fontWeight: 700, color: "var(--gold)" }}>
              {m.user} asks:
            </span>{" "}
            {m.text}
          </div>
        ))}
        {questions.length === 0 && (
          <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>No fan questions yet.</p>
        )}
      </div>
    </div>
  );
}
